import { Injectable, computed, inject, signal } from '@angular/core';
import { webDtos } from '@vectronic/openApi-customer';
import { catchError, tap, throwError } from 'rxjs';
import { RemoteCommandProviderService } from '../remote-command-provider.service';

type ValidationState = 'error' | 'valid' | 'loading' | 'init';

@Injectable({ providedIn: 'root' })
export class ValidationService {
  private remoteCommandProviderService = inject(RemoteCommandProviderService, { optional: true });

  private states = signal<Record<number, ValidationState>>({});
  private errorMessages = signal<Record<number, string>>({});

  errorMessage = computed(() => {
    const messages = this.errorMessages();
    const states = this.states();
    for (const id in states) {
      if (states[id] === 'error') {
        return messages[id] || 'Validation failed';
      }
    }
    return undefined;
  });

  state = computed(() => {
    const states = Object.values(this.states());
    if (states.length === 0) return 'init';
    if (states.includes('error')) return 'error';
    if (states.includes('loading')) return 'loading';
    if (states.every((s) => s === 'valid')) return 'valid';
    return 'init';
  });

  validate(command: webDtos.CustomerCommand) {
    const id = command.idDevice;
    this.states.update((s) => ({ ...s, [id]: 'loading' }));
    this.errorMessages.update((m) => {
      const newMessages = { ...m };
      delete newMessages[id];
      return newMessages;
    });

    return this.remoteCommandProviderService?.validateRemoteCommand(command).pipe(
      catchError((err) => {
        this.states.update((s) => ({ ...s, [id]: 'error' }));
        this.errorMessages.update((m) => ({
          ...m,
          [id]: err?.error?.message ?? 'Validation failed',
        }));
        return throwError(() => err);
      }),
      tap(() => {
        this.states.update((s) => ({ ...s, [id]: 'valid' }));
      }),
    );
  }

  reset() {
    this.states.set({});
    this.errorMessages.set({});
  }
}
